import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
} from 'reactstrap';
import useProjectModelUnit from '../hooks/useProjectModelUnit';
import { useState } from 'react';
import { ModelUnitPerModel } from '../models/modelUnitModel';
import useModalState from '../../../hooks/useModalState';
import SimpleModal from '../../../components/modals/SimpleModal';
import { ModelUnitStatusBadge } from './ModelUnitStatusBadge';
import { ModelUnitStatus } from '../enums/ModelUnitEnum';

interface Props {
  model_id: string;
}

const InvestorProjectModelPendingUnitsModal = ({ model_id }: Props) => {
  const [isOpenModal, toggleModal] = useModalState();
  const [units, setUnits] = useState<ModelUnitPerModel[]>([]);
  const [selectedUnits, setSelectedUnits] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  const toggleUnit = (unitId: string) => {
    const newSelected = new Set(selectedUnits);
    if (newSelected.has(unitId)) {
      newSelected.delete(unitId);
    } else {
      newSelected.add(unitId);
    }
    setSelectedUnits(newSelected);
  };

  const toggleAll = () => {
    if (selectedUnits.size === units.length) {
      setSelectedUnits(new Set());
    } else {
      setSelectedUnits(new Set(units.map((u) => u.id)));
    }
  };

  const {
    apiErrors,
    apiSuccess,
    isLoading,
    doRetrieveUnitsPerModel,
    doActivateUnitsPerModel,
    setApiErrors,
    setApiSuccess,
  } = useProjectModelUnit();

  const clear = () => {
    setSelectedUnits(new Set());
    setApiErrors(null);
    setApiSuccess(null);
    setSearchTerm('');
  };

  // Filter units based on search term
  const filteredUnits = units.filter(
    (unit) =>
      unit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      unit.formatted_price.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const allSelected =
    selectedUnits.size === filteredUnits.length && filteredUnits.length > 0;

  const children = (
    <>
      {units.length > 0 && (
        <>
          <p className="text-muted mb-3">
            Seleccionar las unidades para activar {selectedUnits.size} de{' '}
            {units.length} unidades seleccionadas.
          </p>
          {apiErrors && (
            <Alert
              color="danger"
              isOpen={true}
              toggle={() => setApiErrors(null)}
              fade={true}
            >
              <span className="alert-inner--text">
                <strong>Error!</strong> {apiErrors}
              </span>
            </Alert>
          )}
          {apiSuccess && (
            <Alert
              color="success"
              isOpen={true}
              toggle={() => {
                setApiSuccess(null);
                toggleModal();
              }}
              fade={true}
            >
              <span className="alert-inner--text">
                <strong>{apiSuccess}</strong>
              </span>
            </Alert>
          )}

          {/* Search Bar */}
          <div className="mb-4">
            <Input
              type="text"
              placeholder="Buscar unidades por nombre o precio..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control"
            />
          </div>

          <div className="mb-3 pb-3 border-bottom">
            <div className="d-flex justify-content-between align-items-center">
              <div className="form-check">
                <Input
                  type="checkbox"
                  id="select-all"
                  checked={allSelected}
                  onChange={toggleAll}
                  className="form-check-input"
                />
                <Label
                  check
                  htmlFor="select-all"
                  className="form-check-label fw-medium"
                >
                  Seleccionar todas las unidades
                </Label>
              </div>
              <Button
                color="info"
                disabled={selectedUnits.size === 0}
                onClick={() => {
                  doActivateUnitsPerModel(model_id, Array.from(selectedUnits))
                    .then()
                    .catch();
                }}
              >
                Activar
              </Button>
            </div>
          </div>

          {/* Display filtered units count */}
          {filteredUnits.length > 0 && (
            <p className="text-muted small mb-3">
              Mostrando {filteredUnits.length} de {units.length} unidades
            </p>
          )}

          <Row className="g-4">
            {filteredUnits.map((unit) => {
              const isSelected = selectedUnits.has(unit.id);
              return (
                <Col key={unit.id} xs={12} md={6} lg={3}>
                  <Card
                    className={`cursor-pointer ${
                      isSelected ? 'border-primary' : ''
                    }`}
                    onClick={() => toggleUnit(unit.id)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: isSelected
                        ? 'rgba(13, 110, 253, 0.05)'
                        : 'white',
                    }}
                  >
                    <CardBody className="d-flex gap-2 align-items-start">
                      <Input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleUnit(unit.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1"
                        style={{ cursor: 'pointer' }}
                      />
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                          <span className="mb-0 fw-semibold">
                            Unidad: {unit.name}
                          </span>
                          <ModelUnitStatusBadge
                            status={unit.status as ModelUnitStatus}
                            className="text-white"
                          />
                        </div>
                        <span className="mb-0 fw-semibold text-primary">
                          {unit.formatted_price}
                        </span>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </>
      )}

      {isLoading && <span>Cargando...</span>}

      {units.length === 0 && !isLoading && (
        <span>No hay unidades por activar</span>
      )}
    </>
  );

  return (
    <>
      <Button
        size="sm"
        color="secondary"
        onClick={() => {
          if (model_id) {
            clear();
            doRetrieveUnitsPerModel(model_id, ['created', 'disabled'])
              .then((u) => {
                setUnits(u);
              })
              .catch(console.error);
          }
          toggleModal();
        }}
      >
        <i className="ni ni-collection" />
        <span>Activar unidades</span>
      </Button>
      <SimpleModal
        isOpen={isOpenModal}
        onToggle={toggleModal}
        titleText="Activar unidades"
        children={children}
        size="xl"
      />
    </>
  );
};

export default InvestorProjectModelPendingUnitsModal;
